import React, { useState, useEffect } from 'react';
import { 
  Brain, Volume2, Activity, Eye, Compass, GraduationCap, Briefcase, 
  Cpu, CheckCircle2, Download, Smartphone, Monitor, ShieldCheck, 
  Sparkles, Layers, ArrowRight, Play, VolumeX, Contrast, Type,
  Clock, Award, Stethoscope, ChevronRight, BarChart2, UserCheck, AlertCircle
} from 'lucide-react';
import { 
  DISABILITY_CLUSTERS, 
  DisabilityCategory, 
  getDisabilityCategoryById 
} from '../data/disabilityTaxonomy';
import { DEMO_STUDENTS, DemoStudent } from '../data/demoStudents';
import { UserSession } from '../AppShell';

// Map of module metadata
interface ModuleMeta {
  id: string;
  name: string;
  category: string;
  desc: string;
  filePrefix: string;
}

const MODULE_REGISTRY: Record<string, ModuleMeta> = {
  'aac': { id: 'aac', name: 'AAC Core Dynamic Speech Board', category: 'Augmentative Communication', desc: 'Symbol-based communication matrix with predictive grammar and neural speech', filePrefix: 'AAC_SpeechSynthesizer' },
  'speech-therapy': { id: 'speech-therapy', name: 'Speech Articulation & Waveform Therapy', category: 'Speech Pathology', desc: 'Real-time acoustic spectrogram biofeedback and phonetic drills', filePrefix: 'SpeechTherapy_Articulation' },
  'autism': { id: 'autism', name: 'Autism ABA & Positive Behavior Hub', category: 'Behavioral Health', desc: 'Structured token economies, ABC data tracking, and social visual stories', filePrefix: 'Autism_ABA_Behavior' },
  'sign-language': { id: 'sign-language', name: 'Sign Language & Gesture Studio', category: 'Visual / Hearing', desc: 'Indian Sign Language (ISL) & ASL video dictionary with gesture recognition', filePrefix: 'SignLanguage_Gestures' },
  'braille': { id: 'braille', name: 'Tactile Braille & Screen Reader Companion', category: 'Vision Services', desc: 'Grade 1 & 2 Nemeth Braille code translator with speech audio narrator', filePrefix: 'Braille_Tactile_Learning' },
  'student': { id: 'student', name: 'Student IEP Progress & Self-Service', category: 'Special Education', desc: 'Micro-goal progress tracking, mood checks, and reward star boards', filePrefix: 'Student_IEP_Progress' },
  'therapist': { id: 'therapist', name: 'Therapist SOAP Clinical Notes', category: 'Clinical Operations', desc: 'HIPAA-compliant session documentation with objective goal measurement', filePrefix: 'Therapist_SOAP_Notes' },
  'teacher': { id: 'teacher', name: 'Universal Design for Learning (UDL) Classroom', category: 'Inclusive Education', desc: 'Differentiated lesson planner and sensory break scheduler', filePrefix: 'Teacher_Classroom_Lesson' },
  'assessment': { id: 'assessment', name: 'Diagnostic Clinical Assessments', category: 'Standardized Tests', desc: 'Sensory Profile 2, CARS-2, and developmental milestone evaluators', filePrefix: 'Diagnostic_Clinical_Assessment' },
  'school-erp': { id: 'school-erp', name: 'Special School ERP & Accessible Transit', category: 'Administration', desc: 'Wheelchair van GPS tracking, therapist timetables, and roster management', filePrefix: 'SpecialSchool_ERP_Timetable' },
  'hospital': { id: 'hospital', name: 'Inpatient Rehabilitation Hospital Rounds', category: 'Acute Rehab', desc: 'Multidisciplinary bed manager and bedside therapy coordination', filePrefix: 'RehabHospital_PatientCare' },
  'billing': { id: 'billing', name: 'NDIS & Clinical Insurance Billing', category: 'Financial Compliance', desc: 'Automated claim generation with ICD-10 and NDIS item code validation', filePrefix: 'NDIS_Clinical_Billing' },
  'parent-comm': { id: 'parent-comm', name: 'Caregiver Engagement & Milestone Messenger', category: 'Family Portal', desc: 'Encrypted caregiver messaging with daily therapy victory photos', filePrefix: 'ParentComm_Messenger' },
  'inventory': { id: 'inventory', name: 'Assistive Device Fleet & Wheelchair Inventory', category: 'AT Equipment', desc: 'Wheelchair, AAC iPad, and hearing aid loan manager with calibration alerts', filePrefix: 'AssistiveDevice_Inventory' },
  'analytics': { id: 'analytics', name: 'Executive BI & Clinical Telemetry', category: 'Institutional BI', desc: 'Therapy efficacy charts, attendance compliance, and outcome forecasts', filePrefix: 'ExecutiveAnalytics_BI' },
  'ai': { id: 'ai', name: 'AI Clinical Copilot & SMART Goal Generator', category: 'Clinical AI', desc: 'Automated narrative summarization and evidence-backed IEP prompts', filePrefix: 'AICopilot_ReportSummarizer' },
  'sensory-room': { id: 'sensory-room', name: 'Sensory Room Snoezelen Controller', category: 'Sensory Therapy', desc: 'Interactive room lighting presets, bubble tubes, and calming soundscapes', filePrefix: 'SensoryRoom_Snoezelen_Controller' },
  'tele-rehab': { id: 'tele-rehab', name: 'Encrypted Tele-Rehab Remote Video Clinic', category: 'Tele-Health', desc: 'HD WebRTC video consultations with interactive remote switch-access', filePrefix: 'TeleRehab_RemoteVideo_Therapy' },
  'early-intervention': { id: 'early-intervention', name: 'Early Intervention Milestone Tracker', category: 'Pediatrics', desc: '0-36 month developmental milestone checklists across motor and cognitive domains', filePrefix: 'EarlyIntervention_Developmental_Tracker' },
  'vocational-living': { id: 'vocational-living', name: 'Vocational Independence & Life Skills Studio', category: 'Independent Living', desc: 'ADL task analysis for cooking, transit navigation, and workplace readiness', filePrefix: 'Vocational_IndependentLiving_Skills' },
  'hydrotherapy': { id: 'hydrotherapy', name: 'Aquatic Hydrotherapy & Spasticity Rehab', category: 'Physical Therapy', desc: 'Buoyancy exercise protocols and thermal calibration for spasticity relief', filePrefix: 'Hydrotherapy_AquaticRehab_Controller' },
  'music-therapy': { id: 'music-therapy', name: 'Rhythmic Auditory Stimulation (RAS) Metronome', category: 'Neurologic Music', desc: 'Acoustic pacing metronome (40-180 BPM) for gait timing and Parkinsonian freezing', filePrefix: 'MusicTherapy_RhythmicAuditory_Stimulation' },
  'animal-therapy': { id: 'animal-therapy', name: 'Animal-Assisted Therapy (AAT) Interaction Logger', category: 'Therapy Companions', desc: 'Therapy canine interaction duration, cortisol reduction, and emotional logs', filePrefix: 'AnimalAssisted_Therapy_PetCare' },
  'prosthetics': { id: 'prosthetics', name: 'Bionic Prosthetics & Exoskeleton Fitting', category: 'Bionic Engineering', desc: 'Myoelectric sensor threshold calibration and bionic grip telemetry', filePrefix: 'Prosthetics_Orthotics_Exoskeleton_Fitting' },
  'vbmapp-assessment': { id: 'vbmapp-assessment', name: 'VB-MAPP & ABLLS-R Behavioral Assessment', category: 'Verbal Behavior', desc: '170-milestone developmental battery with multi-domain radar charts', filePrefix: 'VBMAPP_Behavioral_Assessment_Protocol' },
  'core-vocab-ai': { id: 'core-vocab-ai', name: 'LAMP Motor Core Vocab & Predictive Grid', category: 'AAC Motor Planning', desc: 'Fixed Crescendo motor-planning layout with AI predictive phrase suggestions', filePrefix: 'CoreVocab_MotorPlanning_AI_Predictor' },
  'fim-weefim': { id: 'fim-weefim', name: 'FIM & WeeFIM Functional Independence Scoring', category: 'Physical Rehab', desc: '18-item 7-level functional independence measure for motor and cognitive self-care', filePrefix: 'FIM_WeeFIM_Clinical_Scoring' },
  'evv-gps-tracker': { id: 'evv-gps-tracker', name: 'EVV GPS Home Health Visit Verification', category: 'Compliance', desc: 'Federal 21st Century Cures Act compliant caregiver clock-in with geo-fencing', filePrefix: 'EVV_GPS_Caregiver_CheckIn' },
  'gamified-neuro': { id: 'gamified-neuro', name: 'WebGL Biofeedback Neuro-Rehab Games', category: 'Gamified Therapy', desc: 'Sub-millisecond reaction time target games for post-stroke visual-motor recovery', filePrefix: 'Gamified_WebGL_Neuro_Rehab' },
  'transition-matrix': { id: 'transition-matrix', name: 'Secondary Transition & Supported Employment', category: 'Career Readiness', desc: 'IDEA Sec 300.320 vocational interest surveys and supported job-site checklists', filePrefix: 'Secondary_Transition_Vocational_Matrix' }
};

interface DisabilitySuiteViewProps {
  initialDisabilityId?: string;
  currentUser?: UserSession | null;
  onLaunchModule: (moduleId: string) => void;
  onOpenAAC?: () => void;
  onOpenDownloads?: () => void;
  onNavigateToStudentWorkspace?: (studentId: string, clusterId: string) => void;
  onNavigateToAdminWing?: (wingId: string) => void;
  onNavigateTab?: (tab: any) => void;
}

export const DisabilitySuiteView: React.FC<DisabilitySuiteViewProps> = ({
  initialDisabilityId = 'autism-neuro',
  currentUser,
  onLaunchModule,
  onOpenAAC,
  onOpenDownloads,
  onNavigateToStudentWorkspace,
  onNavigateToAdminWing,
  onNavigateTab
}) => {
  const [activeClusterId, setActiveClusterId] = useState<string>(initialDisabilityId);
  const [downloadNotice, setDownloadNotice] = useState<string>('');

  useEffect(() => {
    if (initialDisabilityId && initialDisabilityId !== activeClusterId) {
      setActiveClusterId(initialDisabilityId);
    }
  }, [initialDisabilityId]);

  const handleSelectCluster = (clusterId: string) => {
    setActiveClusterId(clusterId);
    window.history.replaceState({}, '', `/disabilities?cluster=${clusterId}`);
  };
  
  // Interactive Sensory Simulator States
  const [dwellDelay, setDwellDelay] = useState<number>(400);
  const [dwellProgress, setDwellProgress] = useState<number>(0);
  const [dwellSuccess, setDwellSuccess] = useState<boolean>(false);
  const [highContrastActive, setHighContrastActive] = useState<boolean>(false);
  const [dyslexicFontActive, setDyslexicFontActive] = useState<boolean>(false);
  const [speechSampleText, setSpeechSampleText] = useState<string>('AbilityOS clinical speech test');
  const [speechPitch, setSpeechPitch] = useState<number>(1.0);
  const [speechRate, setSpeechRate] = useState<number>(1.0);

  const cluster = getDisabilityCategoryById(activeClusterId) || DISABILITY_CLUSTERS[0];
  const ClusterIcon = cluster.icon;

  // Filter students by this disability cluster
  const matchedStudents = DEMO_STUDENTS.filter(s => {
    const d = s.disabilityCategory.toLowerCase();
    if (activeClusterId === 'autism-neuro') return d.includes('autism') || d.includes('asd');
    if (activeClusterId === 'speech-language') return d.includes('speech') || d.includes('articulation');
    if (activeClusterId === 'locomotor-physical') return d.includes('cerebral palsy') || d.includes('motor');
    if (activeClusterId === 'neurological-stroke') return d.includes('tbi') || d.includes('brain') || d.includes('stroke');
    if (activeClusterId === 'visual-blindness') return d.includes('visual') || d.includes('vision') || d.includes('blind');
    if (activeClusterId === 'hearing-deaf') return d.includes('hearing') || d.includes('deaf');
    if (activeClusterId === 'learning-cognitive') return d.includes('dyslexia') || d.includes('learning');
    return d.includes('transition') || d.includes('vocational');
  });

  const [activeStudentId, setActiveStudentId] = useState<string>(matchedStudents[0]?.id || DEMO_STUDENTS[0].id);

  // Sync student when cluster changes
  useEffect(() => {
    if (matchedStudents.length > 0 && !matchedStudents.some(s => s.id === activeStudentId)) {
      setActiveStudentId(matchedStudents[0].id);
    }
  }, [activeClusterId]);

  const activeStudent = matchedStudents.find(s => s.id === activeStudentId) || matchedStudents[0] || DEMO_STUDENTS[0];

  // Resolve Primary and Secondary Modules
  const primaryModules = cluster.primaryModuleIds
    .map(id => MODULE_REGISTRY[id])
    .filter(Boolean);

  const secondaryModules = cluster.secondaryModuleIds
    .map(id => MODULE_REGISTRY[id])
    .filter(Boolean);

  // Trigger file download
  const handleDownloadBinary = (filename: string, name: string) => {
    setDownloadNotice(`Downloading ${name}... Check your downloads folder.`);
    const link = document.createElement('a');
    link.href = `/downloads/${filename}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => setDownloadNotice(''), 4000);
  };

  // Dwell simulation handlers
  let dwellTimer: any = null;
  const startDwell = () => {
    setDwellSuccess(false);
    let current = 0;
    const step = 20;
    dwellTimer = setInterval(() => {
      current += (step / dwellDelay) * 100;
      if (current >= 100) {
        clearInterval(dwellTimer);
        setDwellProgress(100);
        setDwellSuccess(true);
      } else {
        setDwellProgress(Math.floor(current));
      }
    }, step);
  };

  const cancelDwell = () => {
    if (dwellTimer) clearInterval(dwellTimer);
    setDwellProgress(0);
    setDwellSuccess(false);
  };

  // Speech TTS test
  const speakSample = (text: string) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.pitch = speechPitch;
      u.rate = speechRate;
      window.speechSynthesis.speak(u);
    }
  };

  return (
    <div className="pt-28 pb-16 px-4 sm:px-6 max-w-7xl mx-auto space-y-8 min-h-screen bg-[#f9f6f0]">
      
      {/* 0. PAGE-SPECIFIC WORKSPACE RIBBON */}
      <div className="bg-[#1c1b1f] text-white p-4 sm:p-5 rounded-[28px] border border-white/10 shadow-xl flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-[#ffd64e] text-black flex items-center justify-center font-black shadow-md shrink-0">
            <ClusterIcon className="w-5 h-5 text-black" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#ffd64e] animate-pulse" />
              <span className="text-white/60 uppercase font-black tracking-wider text-[10px]">
                Active Page-Specific Workspace
              </span>
            </div>
            <div className="text-base sm:text-lg font-black text-white flex flex-wrap items-center gap-2 mt-0.5">
              <span>{cluster.title}</span>
              <span className="text-xs font-semibold text-[#ffd64e] bg-white/10 px-2.5 py-0.5 rounded-full border border-white/10">
                Clinical Specialty Suite
              </span>
            </div>
          </div>
        </div>

        {/* Quick Pivot to Connected Workspaces */}
        <div className="flex flex-wrap items-center gap-2 self-start md:self-auto">
          {activeStudent && (
            <button
              onClick={() => onNavigateToStudentWorkspace?.(activeStudent.id, cluster.id)}
              className="px-4 py-2 rounded-full bg-white/10 hover:bg-[#ffd64e] hover:text-black text-white font-bold text-xs transition-all flex items-center gap-1.5 border border-white/15 cursor-pointer shadow-xs group"
              title="Open personalized student portal for this condition"
            >
              <GraduationCap className="w-3.5 h-3.5 text-[#ffd64e] group-hover:text-black" />
              <span>Student Workspace ({activeStudent.name.split(' ')[0]})</span>
              <ChevronRight className="w-3 h-3 opacity-60" />
            </button>
          )}

          <button
            onClick={() => onNavigateToAdminWing?.(cluster.id)}
            className="px-4 py-2 rounded-full bg-white/10 hover:bg-[#ffd64e] hover:text-black text-white font-bold text-xs transition-all flex items-center gap-1.5 border border-white/15 cursor-pointer shadow-xs group"
            title="Open institutional admin wing roster for this condition"
          >
            <Stethoscope className="w-3.5 h-3.5 text-[#ffd64e] group-hover:text-black" />
            <span>Wing Roster</span>
            <ChevronRight className="w-3 h-3 opacity-60" />
          </button>

          {onOpenDownloads && (
            <button
              onClick={onOpenDownloads}
              className="px-4 py-2 rounded-full bg-white/10 hover:bg-[#ffd64e] hover:text-black text-white font-bold text-xs transition-all flex items-center gap-1.5 border border-white/15 cursor-pointer shadow-xs group"
              title="View standalone binaries"
            >
              <Download className="w-3.5 h-3.5 text-[#ffd64e] group-hover:text-black" />
              <span>Binaries</span>
            </button>
          )}
        </div>
      </div>

      {/* 1. TOP DISABILITY SELECTOR MATRIX */}
      <div className="bg-white p-6 sm:p-8 rounded-[36px] border border-black/10 shadow-sm space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-black/10 pb-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1c1b1f] text-[#ffd64e] text-[10px] font-black uppercase tracking-wider">
              <span>RPwD Act 2016 & DSM-5-TR Clinical Architecture</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-black text-[#1c1b1f] mt-1.5 flex items-center gap-2">
              <Stethoscope className="w-7 h-7 text-[#ffd64e]" />
              Clinical Disability Suites
            </h1>
            <p className="text-xs text-slate-600 mt-0.5">
              Select a specialized clinical discipline to configure prescribed interventions, accommodations, and telemetry.
            </p>
          </div>

          <div className="flex items-center gap-2 self-start sm:self-auto">
            <span className="px-3.5 py-1.5 rounded-full bg-[#ffd64e] text-black text-xs font-black shadow-xs">
              8 Clinical Suites Active
            </span>
          </div>
        </div>

        {/* 8 Condition Selector Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {DISABILITY_CLUSTERS.map((c) => {
            const Icon = c.icon;
            const isSelected = activeClusterId === c.id;
            return (
              <button
                key={c.id}
                onClick={() => handleSelectCluster(c.id)}
                className={`p-3.5 rounded-full text-xs font-black transition-all flex items-center justify-center gap-2 border cursor-pointer select-none text-left ${
                  isSelected
                    ? 'bg-[#1c1b1f] text-[#ffd64e] border-[#1c1b1f] shadow-md ring-2 ring-[#ffd64e]/40 scale-[1.01]'
                    : 'bg-[#f9f6f0] text-slate-700 hover:bg-neutral-50 border-black/10'
                }`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span className="truncate">{c.shortName}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 2. CLINICAL SPECIALTY COMMAND DOSSIER */}
      <div className="bg-[#1c1b1f] text-white rounded-[40px] p-8 sm:p-12 border border-white/10 shadow-2xl relative overflow-hidden space-y-8">
        {/* Glow ambient background */}
        <div className="absolute -top-32 right-1/4 w-96 h-96 bg-[#ffd64e]/10 rounded-full blur-3xl pointer-events-none" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start relative z-10">
          
          {/* Left: Department & Diagnostic Taxonomy */}
          <div className="lg:col-span-8 space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="px-3 py-1 rounded-full bg-[#ffd64e] text-black text-[11px] font-black uppercase tracking-wider">
                {cluster.departmentName}
              </span>
              <span className="px-3 py-1 rounded-full bg-white/10 text-white text-[11px] font-mono border border-white/15">
                DSM-5: {cluster.dsmCode}
              </span>
              <span className="px-3 py-1 rounded-full bg-white/10 text-white text-[11px] font-mono border border-white/15">
                ICF: {cluster.icfCode.split(',')[0]}
              </span>
            </div>

            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-[#ffd64e] text-black flex items-center justify-center shrink-0">
                <ClusterIcon className="w-6 h-6" />
              </div>
              <span>{cluster.name}</span>
            </h2>

            <p className="text-slate-300 text-sm leading-relaxed max-w-3xl">
              {cluster.description}
            </p>

            {/* Target Assessments & RPwD Law */}
            <div className="pt-2 flex flex-wrap gap-2 text-xs">
              <span className="text-slate-400 font-bold self-center mr-1">Evaluations:</span>
              {cluster.targetAssessments.map((a, idx) => (
                <span key={idx} className="px-3 py-1 bg-white/10 text-slate-200 rounded-full border border-white/10 font-medium">
                  {a}
                </span>
              ))}
            </div>
          </div>

          {/* Right: Active Student Context Strip */}
          <div className="lg:col-span-4 bg-white/10 backdrop-blur-md p-6 rounded-[28px] border border-white/15 space-y-4">
            <div className="flex items-center justify-between text-xs text-slate-300 border-b border-white/10 pb-2.5">
              <span className="font-bold uppercase tracking-wider">Active Patient Context</span>
              <span className="text-emerald-400 font-mono font-bold">● IEP Active</span>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-[#ffd64e] text-black font-black flex items-center justify-center text-sm shadow">
                {activeStudent.name.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-black text-sm text-white truncate">{activeStudent.name}</div>
                <div className="text-[11px] text-slate-300 font-mono">{activeStudent.rollId} • {activeStudent.grade}</div>
              </div>
            </div>

            <div className="bg-black/40 p-3 rounded-2xl border border-white/10 text-xs space-y-1">
              <div className="text-[10px] text-[#ffd64e] font-bold uppercase tracking-wider">Primary IEP Goal:</div>
              <p className="text-slate-200 text-xs leading-snug line-clamp-2">{activeStudent.iepGoal}</p>
            </div>

            <div className="flex items-center justify-between pt-1 text-xs font-mono">
              <span className="text-slate-300">Mastery Level:</span>
              <span className="text-[#ffd64e] font-extrabold text-sm">{activeStudent.overallMasteryPercent}%</span>
            </div>
          </div>

        </div>

        {/* Clinical Goals Checklist */}
        <div className="pt-6 border-t border-white/10 space-y-3">
          <div className="text-xs font-black uppercase tracking-wider text-[#ffd64e]">
            Standard Clinical Goals & Therapeutic Targets:
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {cluster.clinicalGoals.map((g, idx) => (
              <div key={idx} className="flex items-start gap-2.5 bg-white/5 p-3 rounded-2xl border border-white/5 text-xs text-slate-200">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                <span>{g}</span>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* 3. PRESCRIBED THERAPEUTIC INTERVENTIONS (PRIMARY APPS) */}
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-black/10 pb-3">
          <div>
            <div className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-wider text-black">
              <span className="w-2.5 h-2.5 rounded-full bg-[#ffd64e]" />
              <span>Primary Clinical Interventions ({primaryModules.length} Modules)</span>
            </div>
            <p className="text-xs text-slate-600 mt-0.5">
              Core therapeutic applications prescribed specifically for {cluster.shortName}.
            </p>
          </div>

          <button 
            onClick={() => onNavigateTab('audit')}
            className="text-xs font-bold text-slate-800 hover:text-black underline cursor-pointer self-start sm:self-auto"
          >
            View Active Treatment Audit Logs →
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {primaryModules.map((m) => (
            <div
              key={m.id}
              className="bg-white rounded-[32px] border-2 border-[#ffd64e] p-7 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between space-y-5 relative overflow-hidden group"
            >
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="px-3 py-1 bg-[#1c1b1f] text-[#ffd64e] rounded-full text-[10px] font-black uppercase font-mono tracking-wider">
                    PRIMARY INTERVENTION
                  </span>
                  <span className="px-2.5 py-0.5 bg-amber-100 text-amber-900 rounded-full text-[10px] font-bold">
                    {m.category}
                  </span>
                </div>

                <h3 className="text-lg font-black text-[#1c1b1f] leading-snug group-hover:text-amber-600 transition-colors">
                  {m.name}
                </h3>

                <p className="text-xs text-slate-600 leading-relaxed font-normal">
                  {m.desc}
                </p>
              </div>

              {/* Action Buttons: Launch in Browser + Direct OS Binaries */}
              <div className="space-y-3 pt-3 border-t border-slate-100">
                <button
                  onClick={() => onLaunchModule(m.id)}
                  className="w-full py-2.5 rounded-full bg-[#1c1b1f] hover:bg-[#2d2c31] text-white font-extrabold text-xs flex items-center justify-center gap-1.5 transition-all shadow-sm cursor-pointer"
                >
                  <Play className="w-3.5 h-3.5 fill-[#ffd64e] text-[#ffd64e]" />
                  <span>Launch Interactive Module</span>
                </button>

                <div className="grid grid-cols-3 gap-1.5">
                  <button
                    onClick={() => handleDownloadBinary(`AbilityOS_${m.filePrefix}_Android.apk`, `${m.name} Android APK`)}
                    className="py-1.5 rounded-full text-[10px] font-bold bg-[#f9f6f0] hover:bg-[#ffd64e] text-slate-800 border border-black/10 transition-colors flex items-center justify-center gap-1 cursor-pointer"
                    title="Download Android APK"
                  >
                    <Smartphone className="w-3 h-3" />
                    <span>.APK</span>
                  </button>

                  <button
                    onClick={() => handleDownloadBinary(`AbilityOS_${m.filePrefix}_Windows_Setup.exe`, `${m.name} Windows Setup`)}
                    className="py-1.5 rounded-full text-[10px] font-bold bg-[#f9f6f0] hover:bg-[#ffd64e] text-slate-800 border border-black/10 transition-colors flex items-center justify-center gap-1 cursor-pointer"
                    title="Download Windows Setup EXE"
                  >
                    <Monitor className="w-3 h-3" />
                    <span>.EXE</span>
                  </button>

                  <button
                    onClick={() => handleDownloadBinary(`AbilityOS_${m.filePrefix}_macOS.dmg`, `${m.name} Apple Silicon DMG`)}
                    className="py-1.5 rounded-full text-[10px] font-bold bg-[#f9f6f0] hover:bg-[#ffd64e] text-slate-800 border border-black/10 transition-colors flex items-center justify-center gap-1 cursor-pointer"
                    title="Download macOS DMG"
                  >
                    <Monitor className="w-3 h-3" />
                    <span>.DMG</span>
                  </button>
                </div>
              </div>

            </div>
          ))}
        </div>
      </div>

      {/* 4. CROSS-DISCIPLINARY SUPPORTIVE TOOLS */}
      {secondaryModules.length > 0 && (
        <div className="space-y-4">
          <div className="border-b border-black/10 pb-2">
            <h3 className="text-sm font-black uppercase tracking-wider text-slate-800">
              Cross-Disciplinary Supportive Tools ({secondaryModules.length} Modules)
            </h3>
            <p className="text-xs text-slate-600">
              Assistive accessories that support communication, regulation, and education alongside primary interventions.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {secondaryModules.map((sm) => (
              <div
                key={sm.id}
                className="bg-white p-5 rounded-[28px] border border-black/10 shadow-xs hover:border-black transition-all flex flex-col justify-between space-y-3"
              >
                <div>
                  <span className="text-[9px] font-mono font-bold text-slate-500 uppercase">
                    Supportive AT
                  </span>
                  <h4 className="text-sm font-black text-[#1c1b1f] mt-1">{sm.name}</h4>
                  <p className="text-[11px] text-slate-600 leading-snug mt-1">{sm.desc}</p>
                </div>

                <button
                  onClick={() => onLaunchModule(sm.id)}
                  className="w-full py-2 rounded-full bg-[#f9f6f0] hover:bg-[#1c1b1f] hover:text-white text-[#1c1b1f] font-bold text-[11px] transition-all flex items-center justify-center gap-1 border border-black/10 cursor-pointer"
                >
                  <span>Open Tool</span>
                  <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 5. INTERACTIVE ACCOMMODATIONS & SENSORY SIMULATOR */}
      <div className="bg-white rounded-[36px] p-8 border border-black/10 shadow-sm space-y-6">
        <div className="border-b border-black/10 pb-3">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#ffd64e] text-black text-[10px] font-black uppercase tracking-wider">
            <span>Adaptive Ergonomics Calibration</span>
          </div>
          <h3 className="text-xl font-black text-[#1c1b1f] mt-1 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            Live Sensory & Assistive Accommodation Simulators
          </h3>
          <p className="text-xs text-slate-600">
            Simulate and calibrate the exact clinical accessibility adjustments recommended for {cluster.name}.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          
          {/* Simulator 1: Motor Switch & Eye-Gaze Dwell Calibration */}
          <div className="p-6 rounded-[28px] bg-[#f9f6f0] border border-black/10 flex flex-col justify-between space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-black text-[#1c1b1f]">CP Dwell Switch Simulator</span>
                <span className="text-xs font-mono font-bold text-amber-700">{dwellDelay}ms</span>
              </div>
              <p className="text-[11px] text-slate-600">
                Calibrate selection hold time for individuals with cerebral palsy or involuntary tremors.
              </p>
            </div>

            <div className="text-center py-2">
              <button
                onMouseDown={startDwell}
                onMouseUp={cancelDwell}
                onMouseLeave={cancelDwell}
                onTouchStart={startDwell}
                onTouchEnd={cancelDwell}
                className={`relative w-24 h-24 rounded-full font-black text-xs flex items-center justify-center transition-all select-none mx-auto shadow-inner cursor-pointer ${
                  dwellSuccess 
                    ? 'bg-emerald-500 text-white shadow-lg scale-105' 
                    : 'bg-white text-black border-2 border-black/20 hover:border-[#ffd64e]'
                }`}
              >
                <div 
                  className="absolute inset-0 rounded-full border-4 border-[#ffd64e] transition-all"
                  style={{ clipPath: `polygon(0 0, 100% 0, 100% ${dwellProgress}%, 0 ${dwellProgress}%)` }}
                />
                <span className="relative z-10">{dwellSuccess ? 'Activated!' : dwellProgress > 0 ? `${dwellProgress}%` : 'Hold Dwell'}</span>
              </button>
            </div>

            <div className="space-y-1">
              <div className="flex justify-between text-[10px] text-slate-500 font-mono">
                <span>Fast (200ms)</span>
                <span>Calm (800ms)</span>
              </div>
              <input
                type="range"
                min="200"
                max="800"
                step="50"
                value={dwellDelay}
                onChange={(e) => setDwellDelay(parseInt(e.target.value))}
                className="w-full accent-[#1c1b1f]"
              />
            </div>
          </div>

          {/* Simulator 2: OpenDyslexic & High-Contrast Visual Accommodation */}
          <div className="p-6 rounded-[28px] bg-[#f9f6f0] border border-black/10 flex flex-col justify-between space-y-4">
            <div>
              <div className="text-xs font-black text-[#1c1b1f] mb-1">Visual & Reading Accommodations</div>
              <p className="text-[11px] text-slate-600">
                Invert colors for low-vision learners or enable weighted baseline fonts for dyslexia.
              </p>
            </div>

            <div className="space-y-2.5">
              <button
                onClick={() => {
                  const next = !highContrastActive;
                  setHighContrastActive(next);
                  document.body.classList.toggle('high-contrast', next);
                }}
                className={`w-full p-2.5 rounded-full border text-xs font-black flex items-center justify-between cursor-pointer transition-all ${
                  highContrastActive 
                    ? 'bg-[#1c1b1f] text-[#ffd64e] border-[#1c1b1f]' 
                    : 'bg-white text-slate-800 border-black/10 hover:bg-neutral-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Contrast className="w-3.5 h-3.5 text-amber-500" />
                  <span>High-Contrast (#ffff00 on black)</span>
                </div>
                {highContrastActive && <CheckCircle2 className="w-3.5 h-3.5 text-[#ffd64e]" />}
              </button>

              <button
                onClick={() => {
                  const next = !dyslexicFontActive;
                  setDyslexicFontActive(next);
                  document.body.classList.toggle('font-opendyslexic', next);
                }}
                className={`w-full p-2.5 rounded-full border text-xs font-black flex items-center justify-between cursor-pointer transition-all ${
                  dyslexicFontActive 
                    ? 'bg-[#1c1b1f] text-[#ffd64e] border-[#1c1b1f]' 
                    : 'bg-white text-slate-800 border-black/10 hover:bg-neutral-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Type className="w-3.5 h-3.5 text-indigo-500" />
                  <span>OpenDyslexic Typeface Mode</span>
                </div>
                {dyslexicFontActive && <CheckCircle2 className="w-3.5 h-3.5 text-[#ffd64e]" />}
              </button>
            </div>

            <div className="text-[11px] text-slate-500 bg-white p-3 rounded-2xl border border-black/5 font-mono">
              Status: {highContrastActive ? 'High-Contrast Active' : 'Standard Contrast'} • {dyslexicFontActive ? 'OpenDyslexic Active' : 'System Typography'}
            </div>
          </div>

          {/* Simulator 3: Neural Voice Synthesis & Articulation */}
          <div className="p-6 rounded-[28px] bg-[#f9f6f0] border border-black/10 flex flex-col justify-between space-y-4">
            <div>
              <div className="text-xs font-black text-[#1c1b1f] mb-1">Speech Waveform & Synthesis</div>
              <p className="text-[11px] text-slate-600">
                Calibrate speech rate and pitch for students with apraxia or sensory hypersensitivity.
              </p>
            </div>

            <div className="space-y-2">
              <input
                type="text"
                value={speechSampleText}
                onChange={(e) => setSpeechSampleText(e.target.value)}
                className="w-full p-2.5 rounded-xl border border-black/15 text-xs bg-white text-black font-medium focus:outline-none"
              />

              <div className="grid grid-cols-2 gap-2 text-[10px] font-mono">
                <div className="bg-white p-2 rounded-xl border border-black/5">
                  <div className="text-slate-500">Pitch: {speechPitch.toFixed(1)}x</div>
                  <input
                    type="range"
                    min="0.6"
                    max="1.6"
                    step="0.1"
                    value={speechPitch}
                    onChange={(e) => setSpeechPitch(parseFloat(e.target.value))}
                    className="w-full accent-black"
                  />
                </div>
                <div className="bg-white p-2 rounded-xl border border-black/5">
                  <div className="text-slate-500">Rate: {speechRate.toFixed(1)}x</div>
                  <input
                    type="range"
                    min="0.5"
                    max="1.5"
                    step="0.1"
                    value={speechRate}
                    onChange={(e) => setSpeechRate(parseFloat(e.target.value))}
                    className="w-full accent-black"
                  />
                </div>
              </div>
            </div>

            <button
              onClick={() => speakSample(speechSampleText)}
              className="w-full py-2.5 rounded-full bg-[#ffd64e] hover:bg-[#f5cb3a] text-black font-extrabold text-xs flex items-center justify-center gap-1.5 transition-all shadow-xs cursor-pointer"
            >
              <Volume2 className="w-4 h-4 text-black" />
              <span>Test Neural Vocalization</span>
            </button>
          </div>

        </div>
      </div>

      {downloadNotice && (
        <div className="fixed bottom-6 right-6 z-50 p-4 rounded-full bg-[#1c1b1f] text-[#ffd64e] font-black text-xs border border-[#ffd64e] shadow-2xl flex items-center gap-2 animate-bounce">
          <CheckCircle2 className="w-4 h-4 text-[#ffd64e]" />
          <span>{downloadNotice}</span>
        </div>
      )}

    </div>
  );
};
